import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Search as SearchIcon, X, Hash, Loader2 } from "lucide-react";
import api, { unwrap } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { useAuth } from "@/context/AuthContext";
import UserAvatar from "@/components/UserAvatar";
import PostCard from "@/components/PostCard";

/**
 * Search — 1:1 web port of iOS `src/app/(app)/search.tsx`.
 *
 * Layout:
 *   • Back arrow (40×40 pressable) + search pill (48px, X clear)
 *   • Filter pills (All / People / Posts / Tags) — only when q has value
 *   • Empty prompt when no query · spinner when loading · results otherwise
 *   • Results sections stacked (gap-28): PEOPLE · HASHTAGS · POSTS
 *
 * Endpoints (parallel, 350ms debounce, dropped-response guard):
 *   1) EP.searchAll(q, 1, 10)       — posts + hashtags + additional users
 *   2) EP.searchUsers(q, 1, 10)     — full user list
 *   3) EP.profileByUsername(q)      — exact-@ match
 * Merged/deduped by user id.  Post enrichment mirrors iOS behaviour: flatten
 * `{ post: {...} }` shape and hydrate viewer as author when needed.
 */

const TABS = [
  { key: "all", label: "All" },
  { key: "people", label: "People" },
  { key: "posts", label: "Posts" },
  { key: "tags", label: "Tags" },
];

function EmptyPrompt({ title, subtitle }) {
  return (
    <div className="flex flex-col items-center pt-8">
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center mb-2 bg-secondary"
      >
        <SearchIcon size={24} strokeWidth={1.8} className="text-muted-foreground" />
      </div>
      <h3 className="text-foreground text-[17px] font-bold mt-2">{title}</h3>
      <p className="text-muted-foreground text-[14px] mt-1 text-center max-w-[280px]">
        {subtitle}
      </p>
    </div>
  );
}

export default function Search() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ users: [], posts: [], hashtags: [] });
  const inputRef = useRef(null);

  useEffect(() => {
    // Autofocus the search field on mount, mirroring iOS `autoFocus`
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults({ users: [], posts: [], hashtags: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      const [allR, usersR, exactR] = await Promise.all([
        api.get(EP.searchAll(term, 1, 10)).catch(() => null),
        api.get(EP.searchUsers(term, 1, 10)).catch(() => null),
        api.get(EP.profileByUsername(term)).catch(() => null),
      ]);
      if (cancelled) return;

      const readAll = allR ? unwrap(allR.data) : null;
      const readUsers = usersR ? unwrap(usersR.data) : null;
      const readExact = exactR ? unwrap(exactR.data) : null;

      const usersFromUsers =
        readUsers?.items ||
        readUsers?.users ||
        readUsers?.data?.items ||
        readUsers?.data?.users ||
        (Array.isArray(readUsers) ? readUsers : []);
      const usersFromAll =
        readAll?.users ||
        readAll?.data?.users ||
        [];
      const exact = readExact?.user || readExact?.data || readExact;
      const merged = [];
      const seen = new Set();
      const push = (u) => {
        if (!u || typeof u !== "object") return;
        const k = String(u.id ?? u.user_id ?? u.username ?? "");
        if (!k || seen.has(k)) return;
        seen.add(k);
        merged.push(u);
      };
      usersFromUsers.forEach(push);
      usersFromAll.forEach(push);
      if (exact && (exact.username || exact.user_id || exact.id)) {
        push({
          id: exact.id ?? exact.user_id,
          user_id: exact.user_id ?? exact.id,
          username: exact.username,
          display_name: exact.display_name,
          profile_photo_url: exact.profile_photo_url,
          avatar_url: exact.avatar_url,
          bio: exact.bio,
          is_verified: exact.is_verified,
          follower_count: exact.follower_count,
        });
      }

      const postsRaw = readAll?.posts || readAll?.data?.posts || [];
      const posts = postsRaw.map((raw) => {
        const flat = raw && typeof raw === "object" && raw.post ? { ...raw.post, ...raw } : raw;
        // Hydrate author when missing
        if (!flat.author && !flat.user) {
          const author =
            merged.find((u) => (u.id ?? u.user_id) === flat.user_id) ||
            (user && flat.user_id === user.id
              ? {
                  id: user.id,
                  username: user.username,
                  display_name: user.display_name,
                  profile_photo_url: user.profile_photo_url,
                  avatar_url: user.avatar_url,
                }
              : null);
          if (author) flat.author = author;
        }
        return flat;
      });

      const hashtags = readAll?.hashtags || readAll?.data?.hashtags || [];

      setResults({ users: merged, posts, hashtags });
      setLoading(false);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, user]);

  const showPeople = tab === "all" || tab === "people";
  const showPosts = tab === "all" || tab === "posts";
  const showTags = tab === "all" || tab === "tags";

  const hasQ = q.trim().length > 0;
  const nothing =
    hasQ &&
    !loading &&
    results.users.length === 0 &&
    results.posts.length === 0 &&
    results.hashtags.length === 0;

  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/home", { replace: true }));

  return (
    <div
      className="min-h-[100dvh] w-full bg-background text-foreground flex flex-col"
      data-testid="search-screen"
    >
      {/* Header: back arrow + search pill */}
      <div
        className="flex items-center gap-1.5 pl-2 pr-4 pt-1 pb-3 shrink-0"
        style={{ paddingTop: "calc(4px + env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={back}
          className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary active:opacity-85 transition-all"
          aria-label="Back"
          data-testid="search-back"
        >
          <ChevronLeft size={25} strokeWidth={2.4} />
        </button>

        <div className="flex-1 flex items-center gap-2.5 h-12 rounded-full bg-secondary border border-border px-4">
          <SearchIcon size={18} className="text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search people, posts, hashtags…"
            autoCapitalize="none"
            autoCorrect="off"
            className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground text-[16px] focus:outline-none"
            data-testid="search-input"
          />
          {hasQ && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="shrink-0 hover:opacity-70 transition-opacity"
              aria-label="Clear"
              data-testid="search-clear"
            >
              <X size={18} className="text-muted-foreground" />
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      {hasQ && (
        <div className="px-4 pb-3 shrink-0">
          <div className="flex bg-secondary rounded-[14px] p-1">
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`relative flex-1 h-9 rounded-[10px] flex items-center justify-center transition-opacity ${
                    active ? "" : "opacity-70 hover:opacity-90"
                  }`}
                  data-testid={`search-tab-${t.key}`}
                >
                  {active && (
                    <span className="absolute inset-0 rounded-[10px] bg-card shadow-sm" />
                  )}
                  <span
                    className={`relative text-[13px] ${
                      active ? "text-foreground font-extrabold" : "text-muted-foreground font-semibold"
                    }`}
                  >
                    {t.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 pt-2 pb-20">
        {!hasQ ? (
          <EmptyPrompt
            title="Search BrontoY"
            subtitle="Find people, posts and hashtags across the community."
          />
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 pt-6" data-testid="search-loading">
            <Loader2 size={16} className="animate-spin" style={{ color: "#7638FA" }} />
            <span className="text-muted-foreground text-sm">Searching…</span>
          </div>
        ) : nothing ? (
          <EmptyPrompt title={`No results for "${q.trim()}"`} subtitle="Try a different name, keyword or hashtag." />
        ) : (
          <div className="flex flex-col" style={{ gap: 28 }}>
            {/* PEOPLE */}
            {showPeople && (results.users.length > 0 ? (
              <div>
                <p
                  className="text-muted-foreground font-bold uppercase mb-3"
                  style={{ fontSize: 11, letterSpacing: "1.2px" }}
                >
                  PEOPLE
                </p>
                <div className="flex flex-col gap-3">
                  {results.users.map((u) => (
                    <button
                      key={String(u.id ?? u.user_id ?? u.username)}
                      type="button"
                      onClick={() => navigate(`/${u.username}`)}
                      className="flex items-center gap-3 rounded-[18px] border border-border bg-card px-3 py-3 text-left hover:bg-secondary transition-colors"
                      data-testid={`search-user-${u.id ?? u.user_id}`}
                    >
                      <UserAvatar user={u} size="md" showVerified />
                      <div className="flex-1 min-w-0">
                        <div className="text-foreground text-[15px] font-semibold truncate">
                          {u.display_name || u.username}
                        </div>
                        <div className="text-muted-foreground text-[13px] truncate">
                          @{u.username}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : tab === "people" ? (
              <p className="text-muted-foreground text-sm">No people found.</p>
            ) : null)}

            {/* HASHTAGS */}
            {showTags && (results.hashtags.length > 0 ? (
              <div>
                <p
                  className="text-muted-foreground font-bold uppercase mb-3"
                  style={{ fontSize: 11, letterSpacing: "1.2px" }}
                >
                  HASHTAGS
                </p>
                <div className="flex flex-wrap gap-2">
                  {results.hashtags.map((t, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1.5 h-[38px] px-3.5 rounded-full border border-border"
                      data-testid={`search-hashtag-${i}`}
                    >
                      <Hash size={14} className="text-muted-foreground" />
                      <span className="text-foreground text-sm">
                        {t.name || t.tag || String(t)}
                      </span>
                      {t.post_count != null && (
                        <span className="text-muted-foreground text-xs">· {t.post_count}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : tab === "tags" ? (
              <p className="text-muted-foreground text-sm">No hashtags found.</p>
            ) : null)}

            {/* POSTS */}
            {showPosts && (results.posts.length > 0 ? (
              <div>
                <p
                  className="text-muted-foreground font-bold uppercase mb-3"
                  style={{ fontSize: 11, letterSpacing: "1.2px" }}
                >
                  POSTS
                </p>
                <div className="rounded-[18px] border border-border overflow-hidden">
                  <div className="divide-y divide-border">
                    {results.posts.map((p) => (
                      <PostCard key={String(p.id ?? p.post_id)} post={p} />
                    ))}
                  </div>
                </div>
              </div>
            ) : tab === "posts" ? (
              <p className="text-muted-foreground text-sm">No posts found.</p>
            ) : null)}
          </div>
        )}
      </div>
    </div>
  );
}
